import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { HiMiniMagnifyingGlass } from 'react-icons/hi2';
import { ImSpinner2 } from 'react-icons/im';
import { LuBuilding2, LuHome, LuBriefcase, LuBedDouble, LuMapPin } from 'react-icons/lu';
import PlacesAutocomplete, { geocodeByPlaceId } from 'react-places-autocomplete';
import { CustomerAddress } from 'data-types';
import * as http from '../../../../utils/http';
import * as translator from '../../../../utils/translator';
import * as constants from "../constants";
import { loadGoogleApi } from '../../../Shipping/helpers';

import { MainTracker } from '../../../../PixelTrackers/MainTracker';
import LoadingModal from '../../../../components/Modals/LoadingModal/LoadingModal';
import StickyNotification from '../../../../components/StickyNotification/StickyNotification';
import ExpressDeliveryBlock from './ExpressDeliveryBlock';
import './ShippingAndServiceSelection.css';
import { ChooseTreatmentContext } from '../FreeDosageLayoutV2';
import { useCustomer } from '../../../../customHooks/useCustomer';

const trackAddressError = (errorType: string, context: Record<string, any> = {}) => {
  const userId = context.userId || null;
  const errorData = {
    feature: 'address_express_delivery',
    error_type: errorType,
    user_id: userId,
    ...context,
  };

  window.clientLogger.warn('Address_Feature_Error', {code: 'address_feature_error', errorData});
};

type AddressData = {
  zip: string;
  city?: string | null;
  street?: string | null;
  displayFormat?: string; // Full address string from Google Places (e.g., "Vulkanstr. 5, 10367 Berlin")
};

type Props = {
  customerAddresses: CustomerAddress[];
  onAddressSelected: (address: AddressData, expressStocks: string[], expressStockOptions: any[]) => void;
  country: string;
};

const ADDRESS_SUB_TYPE_ICONS: Record<string, React.ReactElement> = {
  apartment: <LuBuilding2 size={20} />,
  house:     <LuHome size={20} />,
  office:    <LuBriefcase size={20} />,
  hotel:     <LuBedDouble size={20} />,
  other:     <LuMapPin size={20} />,
};

const ZipcodeCheckVariantB: React.FC<Props> = ({
  customerAddresses,
  onAddressSelected,
  country,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isGoogleApiLoaded, setIsGoogleApiLoaded] = useState(false);
  const [isExpressNotInRange, setIsExpressNotInRange] = useState(false);
  const [isZipcodeResolved, setIsZipcodeResolved] = useState(false);
  const [selectedZipcode, setSelectedZipcode] = useState('');
  const [expressStockOptions, setExpressStockOptionsState] = useState<any[]>([]);
  const [placesError, setPlacesError] = useState(false);
  const [notificationRefresh, setNotificationRefresh] = useState(false);
  const [stickyNotification, setStickyNotification] = useState({
    show: false,
    title: '',
    content: '',
    level: 'error',
    showInfoIcon: false,
    autoFade: false,
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hasInitialized = useRef(false);
  const placesAutocompleteRef = useRef<any>(null);

  const chooseTreatmentContext = useContext(ChooseTreatmentContext);
  const { customerState } = useCustomer();

  const showNotification = useCallback((title: string, content: string = '') => {
    setStickyNotification({
      show: true,
      title,
      content,
      level: 'error',
      showInfoIcon: false,
      autoFade: false,
    });
    setNotificationRefresh(prev => !prev);
  }, []);

  // Re-enable autoFade when refresh changes to trigger the effect again
  useEffect(() => {
    if (stickyNotification.show) {
      setStickyNotification(prev => ({
        ...prev,
        autoFade: true
      }));
    }
  }, [notificationRefresh]);

  const language = chooseTreatmentContext?.language;
  const setShippingMethod = chooseTreatmentContext?.setShippingMethod;
  const resetAllCarts = chooseTreatmentContext?.resetAllCarts;
  const selectedExpressTier = chooseTreatmentContext?.selectedExpressTier;
  const handleExpressTierSelect = chooseTreatmentContext?.handleExpressTierSelect;
  const handleSameDayCutoffExpired = chooseTreatmentContext?.handleSameDayCutoffExpired;
  const isUrbifyFlow = chooseTreatmentContext?.isUrbifyFlow;
  const isExpressUberFlow = chooseTreatmentContext?.isExpressUberFlow;
  const isExpressUrbifyFlow = chooseTreatmentContext?.isExpressUrbifyFlow;

  // Filter saved addresses by country
  const filteredAddresses = customerAddresses.filter(
    (addr) => addr.country_id?.toUpperCase() === 'DE'
  );

  // functions for sessionStorage persistence
  const saveAddressSession = useCallback((address: AddressData) => {
    try {
      sessionStorage.setItem('express_delivery_address', JSON.stringify({
        zip: address.zip,
        street: address.street,
        city: address.city,
        displayFormat: address.displayFormat,
      }));
    } catch (err) {
      window.clientLogger.warn('Failed to save address to sessionStorage', err);
    }
  }, []);

  const restoreAddressSession = useCallback(() => {
    try {
      const saved = sessionStorage.getItem('express_delivery_address');
      if (saved) {
        const { zip, street, city, displayFormat } = JSON.parse(saved);
        // Priority: displayFormat (from Google Places) > constructed format > just ZIP
        let displayValue = displayFormat;
        if (!displayValue && (street || city)) {
          // Fallback: construct only from parts that exist (avoid "undefined" in display)
          const parts = [street, zip, city].filter(Boolean);
          displayValue = parts.join(', ');
        }
        if (!displayValue) {
          // Last resort: just show ZIP
          displayValue = zip;
        }
        setInputValue(displayValue);
        setSelectedZipcode(zip);
        setIsZipcodeResolved(true);
      }
    } catch (err) {
      window.clientLogger.warn('Failed to restore address from sessionStorage', err);
    }
  }, []);

  // Load Google Places API for address autocomplete (German-only, R7)
  useEffect(() => {
    loadGoogleApi(() => {
      setIsGoogleApiLoaded(true);
    });

    // R17: Restore saved address on mount
    // (sessionStorage is cleared by StartConsultation page if user logged out)
    restoreAddressSession();
  }, [restoreAddressSession]);

  // Extract zip/street/city from Google's address_components (Place Details)
  const parsePlaceAddressComponents = (components: any[]) => {
    const get = (type: string) => components.find((c) => c.types.includes(type))?.long_name;
    const zip = get('postal_code');
    const route = get('route');
    const streetNumber = get('street_number');
    const city = get('locality') || get('postal_town');
    const street = route ? [route, streetNumber].filter(Boolean).join(' ') : undefined;
    return { zip, street, city };
  };

  // Run eligibility check for express delivery
  const runEligibilityCheck = useCallback(
    async (zipcode: string, addressData?: Omit<AddressData, 'zip'>) => {
      if (!zipcode || zipcode.length !== 5) {
        trackAddressError('invalid_zipcode', {
          userId: customerState?.customerID,
          zipcode,
          zipcodeLength: zipcode?.length,
          action: 'eligibility_check',
        });
        showNotification(
        translator.translate(language, "choose_treatment", "default/invalid_zipcode_or_address"),
        );
        return;
      }

      setIsChecking(true);
      setStickyNotification({ show: false, title: '', content: '', level: 'error', showInfoIcon: false, autoFade: false });

      try {
        const response = await http.postToServer('choose-treatment/get-express-stocks-by-zipcode', { zipcode }, [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.CUSTOMER_ID]);


        const address: AddressData = {
          zip: zipcode,
          ...addressData,
        };

          // Save to sessionStorage for persistence 
        saveAddressSession(address);

        if (response.data?.expressStocks?.length > 0) {

          setIsExpressNotInRange(false);
          setSelectedZipcode(zipcode);
          setExpressStockOptionsState(response.data?.expressStockOptions || []);
          setIsZipcodeResolved(true);
          onAddressSelected(address, response.data?.expressStocks || [], response.data?.expressStockOptions || []);
        } else {
          setIsExpressNotInRange(true);
          setIsZipcodeResolved(false);
          setExpressStockOptionsState([]);
          // Don't call onAddressSelected when not eligible - just show form + unavailable message
        }
      } catch (err: any) {
        const errorMsg = err?.message || 'Failed to check address eligibility';
        trackAddressError('api_error', {
          userId: customerState?.customerID,
          zipcode,
          errorMessage: errorMsg,
          errorCode: err?.code,
          errorStatus: err?.status,
          action: 'get_express_stocks_by_zipcode',
        });
        showNotification(
          translator.translate(language, "choose_treatment", "default/couldnt_check_express_delivery"),
        );
      } finally {
        setIsChecking(false);
      }
    },
    [country, onAddressSelected]
  );

  // Auto-fill and auto-check default address on mount
  // Priority: sessionStorage > default address (R17)
  useEffect(() => {
    if (!hasInitialized.current) {
      // Check if address exists in sessionStorage
      const savedAddress = sessionStorage.getItem('express_delivery_address');
      if (savedAddress) {
        // Address already restored by restoreAddressSession(), don't override
        hasInitialized.current = true;
        return;
      }

      // No saved session address, use default if available
      const defaultAddress = filteredAddresses.find((addr) => addr.is_default);
      if (defaultAddress?.zip) {
        hasInitialized.current = true;
        const displayFormat = `${defaultAddress.street} ${defaultAddress.house_number}, ${defaultAddress.zip} ${defaultAddress.city}`;
        const addressData = {
          city: defaultAddress.city,
          street: defaultAddress.street,
          displayFormat,
        };
        setInputValue(displayFormat);
        // R17: Save default address to sessionStorage on first mount
        saveAddressSession({
          zip: defaultAddress.zip,
          ...addressData,
        });
        runEligibilityCheck(defaultAddress.zip, addressData);
      }
    }
  }, [filteredAddresses, runEligibilityCheck, saveAddressSession]);

  // Handle saved address selection
  const handleAddressSelect = (address: CustomerAddress) => {
    const displayFormat = `${address.street}, ${address.zip} ${address.city}`;
    setStickyNotification({ show: false, title: '', content: '', level: 'error', showInfoIcon: false, autoFade: false });
    runEligibilityCheck(address.zip, {
      city: address.city,
      street: address.street,
      displayFormat,
    });
    setShowDropdown(false);
    setInputValue(displayFormat);
  };

  // Geocode ZIP code to extract city/street (R17: store complete address)
  const extractAddressFromZip = useCallback(async (zipcode: string) => {
    try {
      if (!(window as any).google?.maps?.Geocoder) {
        window.clientLogger.warn('Google Geocoder not available yet');
        return { city: undefined, street: undefined, zip: zipcode };
      }

      const geocoder = new (window as any).google.maps.Geocoder();
      const result = await new Promise<any>((resolve, reject) => {
        geocoder.geocode(
          { address: zipcode, componentRestrictions: { country: 'DE' } },
          (results: any, status: string) => {
            if (status === 'OK' && results?.length > 0) {
              resolve(results[0]);
            } else {
              trackAddressError('geocoding_failed', {
                userId: customerState?.customerID,
                zipcode,
                geocodingStatus: status,
                resultsLength: results?.length,
                action: 'extract_address_from_zip',
              });
              reject(new Error(`Geocoding failed: ${status}`));
            }
          }
        );
      });
      const addressData = parsePlaceAddressComponents(result.address_components);
      return addressData;
    } catch (err: any) {
      trackAddressError('extract_address_failed', {
        userId: customerState?.customerID,
        zipcode,
        errorMessage: err?.message,
        action: 'extract_address_from_zip',
      });
      // Return ZIP only if geocoding fails - will still run eligibility check
      return { city: undefined, street: undefined, zip: zipcode };
    }
  }, [customerState?.customerID]);

  // Handle direct ZIP code input (5 digits) — also fed by PlacesAutocomplete's onChange(value: string)
  const handleInputChange = (value: string) => {
    setInputValue(value);
    setPlacesError(false);

    // Reopen dropdown when user starts typing (allow new search)
    if (value.length > 0 && !showDropdown) {
      setShowDropdown(true);
    }

    // Auto-check if 5 digits are entered (direct ZIP)
    if (/^\d{5}$/.test(value)) {
      setShowDropdown(false);
      // Extract city/street from ZIP async (R17)
      extractAddressFromZip(value).then((addressData) => {
        runEligibilityCheck(value, { city: addressData.city, street: addressData.street, displayFormat: value });
      });
    }
  };

  // Handle Google Places suggestion selection
  const handlePlacesSuggestionSelect = async (address: string, placeId: string) => {
    setShowDropdown(false);
    setInputValue(address);

    try {
      const [place] = await geocodeByPlaceId(placeId);
      const { zip, street, city } = parsePlaceAddressComponents(place.address_components);

      if (!zip) {
        trackAddressError('no_zip_in_places_suggestion', {
          userId: customerState?.customerID,
          address,
          placeId,
          action: 'select_places_suggestion',
        });
        showNotification(
          translator.translate(language, "choose_treatment", "default/couldnt_identify_zipcode_from_address_enter_manually"),
        );
        return;
      }

      runEligibilityCheck(zip, { city, street, displayFormat: address });
    } catch (err: any) {
      trackAddressError('place_selection_failed', {
        userId: customerState?.customerID,
        address,
        placeId,
        errorMessage: err?.message || 'geocodeByPlaceId failed',
        action: 'select_places_suggestion',
      });
      showNotification(
          translator.translate(language, "choose_treatment", "default/couldnt_load_address_select_again"),
      );
    }
  };

  const handlePlacesError = (status: string, clearSuggestions: () => void) => {
    window.clientLogger.warn('PlacesAutocomplete error:', { status });
    if (status !== 'ZERO_RESULTS') {
      window.clientLogger.warn('Google Maps API returned error with status: ' + status);
      setPlacesError(true);
    } else {
      setPlacesError(false);
    }
    clearSuggestions();
  };

  // Handle search button click (manual ZIP/address submission)
  const handleSearchClick = () => {
    setStickyNotification({ show: false, title: '', content: '', level: 'error', showInfoIcon: false, autoFade: false });
    const zipMatch = inputValue.match(/\d{5}/);

    // If input is just a ZIP code, geocode by ZIP
    if (zipMatch && /^\d{5}$/.test(inputValue.trim())) {
      extractAddressFromZip(zipMatch[0]).then((addressData) => {
        runEligibilityCheck(zipMatch[0], { city: addressData.city, street: addressData.street, displayFormat: inputValue });
      });
    }
    // If input is a full address, try to geocode it
    else if (inputValue.trim().length > 5) {
      setIsChecking(true);
      const geocoder = new (window as any).google.maps.Geocoder();
      geocoder.geocode(
        { address: inputValue, componentRestrictions: { country: 'DE' } },
        (results: any, status: string) => {
          setIsChecking(false);
          if (status === 'OK' && results?.length > 0) {
            const { zip, street, city } = parsePlaceAddressComponents(results[0].address_components);
            if (zip) {
              runEligibilityCheck(zip, { city, street, displayFormat: inputValue });
            } else {
              trackAddressError('no_zip_in_address', {
                userId: customerState?.customerID,
                inputValue,
                action: 'search_address',
              });
              showNotification(
                translator.translate(language, "choose_treatment", "default/coludnt_recongize_address_or_zipcode"),
              );
            }
          } else {
            trackAddressError('search_geocoding_failed', {
              userId: customerState?.customerID,
              inputValue,
              geocodingStatus: status,
              resultsLength: results?.length,
              action: 'search_address',
            });
            showNotification(
              translator.translate(language, "choose_treatment", "default/check_address_spelling"),
            );
          }
        }
      );
    }
    else {
      trackAddressError('invalid_input_format', {
        userId: customerState?.customerID,
        inputValue,
        action: 'search_button_click',
      });
      showNotification(
        translator.translate(language, "choose_treatment", "default/invalid_zipcode_or_address"),
      );
    }
  };

  // Handle input focus - show dropdown and re-trigger search if text exists (R6: show for all users)
  const handleInputFocus = () => {
    setShowDropdown(true);
    setPlacesError(false);

    // PlacesAutocomplete only fetches suggestions on the native input onChange event,
    // not when `value` changes programmatically - so refocusing an already-filled input
    // (whose suggestions were cleared on blur) won't re-search on its own.
    // Call the library's internal fetchPredictions directly to force a fresh search.
    if (inputValue.trim().length > 0) {
      placesAutocompleteRef.current?.fetchPredictions();
    }

    setTimeout(() => {
      inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  };

  // Handle geolocation auto-find
  const handleGeolocation = useCallback(() => {
    if (!navigator.geolocation) {
      return;
    }

    const userId = customerState?.customerID || null;

    // Track Geo_Click event (user clicks "Use my location" button)
    MainTracker.track("custom", "Geo_Click", {
      user_id: userId,
      geo_source: "button",
      screen: "express_shipping"
    });

    setIsChecking(true);
    setStickyNotification({ show: false, title: '', content: '', level: 'error', showInfoIcon: false, autoFade: false });
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude, accuracy } = position.coords;

          // Track Geo_Permission_Result event (permission granted)
          MainTracker.track("custom", "Geo_Permission_Result", {
            user_id: userId,
            geo_permission: 'granted',
            lat_lng_precision: accuracy || null,
            geo_source: 'browser'
          });

          // Track geo_confirmed event
          MainTracker.track("custom", "geo_confirmed", {
            user_id: userId,
            latitude: latitude,
            longitude: longitude,
            geo_source: 'browser'
          });

          // Declare variables outside try/catch for scope
          const requestStartTime = Date.now();
          const requestId = `geo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

          http.postToServer('choose-treatment/get-express-stocks-by-long-lat', { latitude, longitude }, [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.CUSTOMER_ID])
            .then((response) => {
              const latencyMs = Date.now() - requestStartTime;
              const expressStocks = response.data?.expressStocks;
              const zipcode = response.data?.zipcode;
              const zipPrefix = zipcode ? zipcode.substring(0, 3) : null;

              // Track Uber_Eligibility_Request event after API response
              MainTracker.track('custom', 'Uber_Eligibility_Request', {
                user_id: userId,
                zip_prefix: zipPrefix,
                geo_source: 'geo',
                request_id: requestId,
                latency_ms: latencyMs
              });

              const zipCityPart = `${zipcode} ${response.data?.city || ''}`.trim();
              const address = {
                zip: zipcode,
                city: response.data?.city,
                street: response.data?.street,
                displayFormat: [response.data?.street, zipCityPart].filter(Boolean).join(', '),
              };
              const displayFormat = address.displayFormat;

              setInputValue(displayFormat);
              setSelectedZipcode(zipcode);

              saveAddressSession(address);

              if (expressStocks && expressStocks.length > 0 && response.data?.expressStockOptions?.length) {
                // Address is eligible
                setExpressStockOptionsState(response.data?.expressStockOptions || []);
                setIsZipcodeResolved(true);
                setIsExpressNotInRange(false);
                onAddressSelected(address, response.data?.expressStocks || [], response.data?.expressStockOptions || []);
              } else {
                // Address is not eligible
                setIsZipcodeResolved(false);
                setIsExpressNotInRange(true);
                setExpressStockOptionsState([]);
              }
            })
            .catch((error) => {
              const latencyMs = Date.now() - requestStartTime;

              // Track Uber_Eligibility_Request event even on error
              MainTracker.track('custom', 'Uber_Eligibility_Request', {
                user_id: userId,
                zip_prefix: null,
                geo_source: 'geo',
                request_id: requestId,
                latency_ms: latencyMs
              });

              trackAddressError('geolocation_api_error', {
                userId,
                errorMessage: error?.message,
                errorStatus: error?.status,
                errorCode: error?.code,
                latencyMs,
                action: 'get_express_stocks_by_coordinates',
              });

              showNotification(
                translator.translate(language, "choose_treatment", "default/couldnt_check_express_delivery"),
              );
            });
        } catch (error: any) {
          trackAddressError('geolocation_processing_error', {
            userId,
            errorMessage: error?.message,
            action: 'geolocation_success_callback',
          });
        } finally {
          // prevent too many calls
          setTimeout(() => {
            setIsChecking(false);
          }, 1000);
        }
      },
      (error) => {
        let errorType = 'geolocation_unknown_error';
        if (error.code === error.PERMISSION_DENIED) {
          errorType = 'geolocation_permission_denied';
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          errorType = 'geolocation_position_unavailable';
        } else if (error.code === error.TIMEOUT) {
          errorType = 'geolocation_timeout';
        }

        trackAddressError(errorType, {
          userId: customerState?.customerID,
          errorCode: error.code,
          errorMessage: error.message,
          action: 'geolocation',
        });

        // Track Geo_Permission_Result event (permission denied or other error)
        const geoPermission = error.code === error.PERMISSION_DENIED ? 'denied' : 'denied';
        MainTracker.track("custom", "Geo_Permission_Result", {
          user_id: userId,
          geo_permission: geoPermission,
          lat_lng_precision: null,
          geo_source: 'browser'
        });

        showNotification(
          translator.translate(language, "choose_treatment", "default/error_getting_location"),
          translator.translate(language, "choose_treatment", "default/error_getting_location_description")
        );
        setIsChecking(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  }, [runEligibilityCheck, customerState?.customerID, language, showNotification]);

  const handleClearZipcode = () => {
    setIsZipcodeResolved(false);
    setSelectedZipcode('');
    setExpressStockOptionsState([]);
    setIsExpressNotInRange(false);
    setPlacesError(false);
    setStickyNotification({ show: false, title: '', content: '', level: 'error', showInfoIcon: false, autoFade: false });
    // Notify to hide products when clearing zipcode
    onAddressSelected({ zip: '', city: '', street: '' }, [], []);
  };

  const handleFallbackShipping = () => {
    setShippingMethod?.(constants.SHIPPING);
    resetAllCarts?.();
    setIsExpressNotInRange(false);
    handleClearZipcode();
  };

  const handleFallbackPickup = () => {
    setShippingMethod?.(constants.PICKUP);
    resetAllCarts?.();
    setIsExpressNotInRange(false);
    handleClearZipcode();
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const renderSavedAddresses = () => {
    const sortedAddresses = [...filteredAddresses].sort((a, b) => {
      if (a.is_default === b.is_default) return 0;
      return a.is_default ? -1 : 1;
    });

    return filteredAddresses.length > 0 && (
      <div className="flex flex-col gap-[7px] w-full">
        <span className="text-[#99A3B2] text-xs font-roboto leading-4 w-full">
          {translator.translate(language, "choose_treatment", "default/zipcode_saved_addresses_label")}
        </span>
        <div className="flex flex-col gap-[7px] pr-2 h-[245px] overflow-y-auto ">
          {sortedAddresses.map((address) => {
            const icon = ADDRESS_SUB_TYPE_ICONS[address.address_sub_type || "apartment"];
            return (
              <div
                key={address.id}
                onClick={() => handleAddressSelect(address)}
                className="bg-white h-14 rounded-md flex items-center gap-3 pl-3 pr-5 py-3 cursor-pointer w-full shrink-0"
                style={{ border: '1px solid #DBE1EB' }}
              >
                <div className="w-5 h-5 text-[#3E3E3E] shrink-0 flex items-center justify-center">
                  {icon}
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-[#3E3E3E] text-sm font-medium font-roboto leading-[18px]">
                    {address.street} {address.house_number}
                  </span>
                  <span className="text-[#666666] text-xs font-roboto leading-4">
                    {address.city} - {address.zip}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderPlacesSuggestions = (suggestions: any[], getSuggestionItemProps: any, loading: boolean) => {
    return (
      <div className="flex flex-col w-full">
        {/* Loading state */}
        {loading && (
          <div className="bg-white flex gap-4 h-15 items-center overflow-hidden p-3 border-b border-gray-200">
            <ImSpinner2 className="w-4 h-4 text-[#99A3B2] animate-spin shrink-0" />
            <p className="text-sm font-poppins text-[#99A3B2] m-0 whitespace-nowrap">
              {translator.translate(language, "choose_treatment", "default/zipcode_searching_for_addresses")}
            </p>
          </div>
        )}

        {/* Service error state (R13) */}
        {placesError && (
          <div className="p-3 bg-red-50 border-b border-red-200">
            <p className="text-sm font-poppins text-red-600 m-0">
              {translator.translate(language, "choose_treatment", "default/zipcode_service_error")}
            </p>
          </div>
        )}

        {/* Suggestions list */}
        {suggestions.map((suggestion: any) => (
          <div
            key={suggestion.placeId}
            {...getSuggestionItemProps(suggestion)}
            className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-200 last:border-b-0"
          >
            <div className="font-medium text-gray-900">{suggestion.description}</div>
          </div>
        ))}

        {/* No addresses found state */}
        {!loading && suggestions.length === 0 && inputValue.length > 1 && (
          <div className="p-3">
            <p className="font-poppins text-[14px] leading-[18px] m-0">
              <span className="font-medium text-[#08080A]">
                {translator.translate(language, "choose_treatment", "default/zipcode_no_addresses_found")}
              </span>
              <br />
              <span className="font-normal text-[#99A3B2]">
                {translator.translate(language, "choose_treatment", "default/zipcode_no_addresses_found_description")}
              </span>
            </p>
          </div>
        )}
      </div>
    );
  };

  const placesSearchOptions = {
    componentRestrictions: { country: 'de' },
    types: ['address'],
  };

  // Show ExpressDeliveryBlock when eligible (R15)
  if (!isExpressNotInRange && isZipcodeResolved && isUrbifyFlow && expressStockOptions.length > 0 && (isExpressUberFlow || isExpressUrbifyFlow) && handleExpressTierSelect && handleSameDayCutoffExpired) {
    return (
      <ExpressDeliveryBlock
        key={selectedZipcode}
        zipcode={selectedZipcode}
        expressStockOptions={expressStockOptions}
        customerID={customerState?.customerID}
        selectedTier={selectedExpressTier || null}
        onTierSelect={handleExpressTierSelect}
        onClearZipcode={handleClearZipcode}
        onFallbackShipping={handleFallbackShipping}
        onFallbackPickup={handleFallbackPickup}
        onCountdownExpired={handleSameDayCutoffExpired}
        language={language || 'de'}
        isExpressUberFlow={isExpressUberFlow || false}
        isExpressUrbifyFlow={isExpressUrbifyFlow || false}
      />
    );
  }

  return (
    <>
      <StickyNotification
        showNotification={stickyNotification.show}
        title={stickyNotification.title}
        onClick={() => setStickyNotification({ show: false, title: "", content: "", level: "error", showInfoIcon: false, autoFade: false })}
        content={stickyNotification.content}
        level={stickyNotification.level}
        autoFade={stickyNotification.autoFade}
        refresh={notificationRefresh}
      />

      <div className="flex flex-col gap-3">
        {isChecking && <LoadingModal />}

      {/* Divider */}
      <hr style={{ border: 'none', borderTop: '0.5px solid #DEE3ED', margin: 0 }} />

      {/* Header and description - hide when not eligible */}
      {!isExpressNotInRange && (
        <div className="flex flex-col gap-1 text-center">
          <span className="text-[15px] font-semibold font-poppins text-[#0D2C54]">
            {translator.translate(language, "choose_treatment", "default/zipcode_check_header")}
          </span>
          <p className="text-[#98A0AF] font-roboto text-xs m-0">
            {translator.translate(language, "choose_treatment", "default/zipcode_check_description")}
          </p>
        </div>
      )}

      <div className="flex flex-col gap-2 w-full">
        {/* Input field + Search button row */}
        <div className="flex gap-1.5 items-stretch w-full">
          <div className="flex-1 relative min-w-0">
            {isGoogleApiLoaded ? (
              <PlacesAutocomplete
                ref={placesAutocompleteRef}
                value={inputValue}
                onChange={handleInputChange}
                onSelect={handlePlacesSuggestionSelect}
                onError={handlePlacesError}
                searchOptions={placesSearchOptions}
                debounce={300}
              >
                {({ getInputProps, suggestions, getSuggestionItemProps, loading }: any) => {
                  return (
                    <>
                      <input
                      {...getInputProps({
                        placeholder: 'PLZ oder Adresse eingeben',
                        autoComplete: 'off',
                        className:
                          'w-full h-[60px] px-4 border border-solid border-[#DEE3ED] rounded-2xl bg-white font-poppins text-base text-black placeholder-[#99A3B2] focus:outline-none focus:ring-0',
                      })}
                      ref={inputRef}
                      data-1p-ignore
                      onFocus={handleInputFocus}
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter' && !isChecking) {
                          handleSearchClick();
                        }
                      }}
                    />

                      {(() => {
                        const shouldShow = showDropdown && (filteredAddresses.length > 0 || suggestions.length > 0 || loading || inputValue);
                        return shouldShow && (
                        <div
                          ref={dropdownRef}
                          className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl p-3 shadow-[0px_12px_24px_0px_rgba(5,27,55,0.08),0px_0px_1px_0px_rgba(5,27,55,0.16)] z-50 max-h-80 overflow-y-auto"
                        >
                          {!inputValue && renderSavedAddresses()}
                          {(inputValue || suggestions.length > 0 || loading) && renderPlacesSuggestions(suggestions, getSuggestionItemProps, loading)}
                        </div>
                        );
                      })()}
                    </>
                  );
                }}
              </PlacesAutocomplete>
            ) : (
              <>
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onFocus={handleInputFocus}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isChecking) {
                      handleSearchClick();
                    }
                  }}
                  placeholder="PLZ oder Adresse eingeben"
                  autoComplete="off"
                  data-1p-ignore
                  className="w-full h-[60px] px-4 border border-[#C2C9D6] rounded-2xl bg-white font-poppins text-base text-black placeholder-[#99A3B2] focus:outline-none focus:ring-1 focus:ring-[#269C6E]"
                />

                {showDropdown && ((!inputValue && filteredAddresses.length > 0) || inputValue) && (
                  <div
                    ref={dropdownRef}
                    className="absolute top-full left-0 right-0 mt-2 bg-white border border-[#C2C9D6] rounded-2xl shadow-lg z-50 max-h-80 overflow-y-auto"
                  >
                    {!inputValue && renderSavedAddresses()}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Search button - Figma primary gradient (#269c6e -> #7aba47) */}
          <button
            id="zipcode-search-button-variant-b"
            onClick={handleSearchClick}
            disabled={isChecking}
            style={{ background: 'linear-gradient(135deg, #269C6E 0%, #7ABA47 100%)' }}
            className={`h-[60px] w-[60px] hover:opacity-85 shrink-0 border-0 ${isChecking ? 'cursor-wait' : 'cursor-pointer'} outline-none text-white rounded-2xl flex items-center justify-center disabled:bg-gray-400 disabled:bg-none transition-colors`}
          >
            <HiMiniMagnifyingGlass className="w-6 h-6" />
          </button>
        </div>

        {/* "Mein Standort" */}
        <div className="w-full flex justify-center mt-4 mb-2">
          <button
            onClick={handleGeolocation}
            disabled={isChecking}
            className="inline-flex items-center border-none bg-transparent gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
              <path
                d="M15.8438 0.0779145C16.153 -0.0685401 16.5209 -0.00470138 16.7628 0.23721C17.0047 0.479122 17.0685 0.84704 16.9221 1.15622L9.63639 16.5371C9.49001 16.8461 9.16513 17.0297 8.8249 16.996C8.48471 16.9623 8.20228 16.7186 8.11933 16.3869L6.61808 10.3819L0.613078 8.88067C0.281439 8.79772 0.0376792 8.51529 0.0039608 8.1751C-0.0297288 7.83487 0.153908 7.50999 0.462874 7.36361L15.8438 0.0779145ZM3.20054 7.85849L7.48215 8.92889L7.53551 8.94431C7.79968 9.02996 8.0031 9.24594 8.07111 9.51785L9.14112 13.7991L14.488 2.51162L3.20054 7.85849Z"
                fill="#61A333"
              />
            </svg>
            <span className="font-poppins font-medium text-[14px]  leading-none text-[#61A333] hover:opacity-85 underline whitespace-nowrap">
              {translator.translate(language, "choose_treatment", "default/use_current_location")}
            </span>
          </button>
        </div>

        {/* Express delivery not available fallback */}
        {isExpressNotInRange && handleExpressTierSelect && handleSameDayCutoffExpired &&
          (() => {
            return <ExpressDeliveryBlock
            key={selectedZipcode}
            zipcode={selectedZipcode}
            expressStockOptions={expressStockOptions}
            customerID={customerState?.customerID}
            selectedTier={selectedExpressTier || null}
            onTierSelect={handleExpressTierSelect}
            onClearZipcode={handleClearZipcode}
            onFallbackShipping={handleFallbackShipping}
            onFallbackPickup={handleFallbackPickup}
            onCountdownExpired={handleSameDayCutoffExpired}
            language={language || 'de'}
            isExpressUberFlow={isExpressUberFlow || false}
            isExpressUrbifyFlow={isExpressUrbifyFlow || false}
            titleClassName={"!text-sm !font-semibold"}
            shippingMethodClassName={"!text-sm !font-medium !font-poppins"}
            collectionMethodClassName={"!text-sm !font-medium !font-poppins"}
            />
          })()
        }


      </div>
      </div>
    </>
  );
};

export default ZipcodeCheckVariantB;
